<template>
  <div class="memos-page">
    <div class="page-toolbar d-flex flex-wrap align-items-end justify-content-between mb-4">
      <div class="d-flex flex-wrap align-items-end toolbar-filters">
        <div class="filter-group filter-search">
          <label for="memo-search">Search</label>
          <div class="search-wrap">
            <i class="bi bi-search"></i>
            <input
              id="memo-search"
              v-model="searchQuery"
              type="search"
              class="form-control"
              placeholder="Title or memo number…"
              autocomplete="off"
            />
          </div>
        </div>
        <div class="filter-group">
          <label for="memo-filter-year">Year</label>
          <select id="memo-filter-year" v-model="filterYear" class="form-control">
            <option value="">All years</option>
            <option v-for="y in availableYears" :key="y" :value="y">{{ y }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label for="memo-filter-type">Type</label>
          <select id="memo-filter-type" v-model="filterType" class="form-control">
            <option value="">All types</option>
            <option value="office">Office Memorandum</option>
            <option value="hei">HEI Memorandum</option>
          </select>
        </div>
        <div class="filter-group">
          <label for="memo-filter-month">Month</label>
          <select id="memo-filter-month" v-model="filterMonth" class="form-control">
            <option value="">All months</option>
            <option
              v-for="opt in monthOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
      </div>
      <div class="toolbar-meta d-flex align-items-center" style="gap:.6rem;">
        <span class="result-count">
          {{ filteredMemos.length }}
          {{ filteredMemos.length === 1 ? "memo" : "memos" }}
        </span>
        <div class="view-toggle" role="group">
          <button
            type="button"
            class="toggle-btn"
            :class="{ active: viewMode === 'list' }"
            title="List view"
            @click="viewMode = 'list'"
          >
            <i class="bi bi-list-ul"></i>
          </button>
          <button
            type="button"
            class="toggle-btn"
            :class="{ active: viewMode === 'grid' }"
            title="Grid view"
            @click="viewMode = 'grid'"
          >
            <i class="bi bi-grid-3x3-gap"></i>
          </button>
        </div>
        <button
          v-if="canManage"
          type="button"
          class="btn btn-primary"
          @click="$refs.CreateMemorandumModal.openCreate()"
        >
          <i class="bi bi-plus-lg mr-1"></i> Issue Memorandum
        </button>
      </div>
    </div>

    <div v-if="tableLoader" class="text-center py-5 text-muted">
      <span class="spinner-border spinner-border-sm mr-2"></span>
      Loading memorandums…
    </div>

    <div v-else-if="!filteredMemos.length" class="empty-state">
      <i class="bi bi-journal-text empty-icon"></i>
      <p class="mb-0">No memorandums match your filters.</p>
    </div>

    <!-- ── LIST VIEW ── -->
    <div v-else-if="viewMode === 'list'" class="table-responsive list-view-wrap">
      <table class="table table-hover list-table mb-0">
        <thead>
          <tr>
            <th style="width:160px">Memo No.</th>
            <th style="width:110px">Type</th>
            <th>Title</th>
            <th style="width:160px">Recipient</th>
            <th style="width:120px">Date Issued</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="memo in filteredMemos"
            :key="memo.id"
            class="list-row"
            @click="openDetail(memo)"
            role="button"
            tabindex="0"
            @keydown.enter="openDetail(memo)"
          >
            <td>
              <span class="memo-badge">
                No. {{ String(memo.number).padStart(3, "0") }} s. {{ seriesYear(memo.date_issued) }}
              </span>
            </td>
            <td>
              <span class="memo-type-badge" :class="'type-' + (memo.type || 'office')">
                {{ memo.type === "hei" ? "HEI" : "Office" }}
              </span>
            </td>
            <td>
              <div class="list-title">{{ memo.title }}</div>
            </td>
            <td class="small text-muted">{{ memo.recipient || '—' }}</td>
            <td class="small text-nowrap">{{ memo.date_issued | shortDate }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ── GRID VIEW ── -->
    <div v-else class="memo-grid">
      <article
        v-for="memo in filteredMemos"
        :key="memo.id"
        class="memo-card"
        @click="openDetail(memo)"
        role="button"
        tabindex="0"
        @keydown.enter="openDetail(memo)"
        @keydown.space.prevent="openDetail(memo)"
      >
        <div class="memo-card-top">
          <span class="memo-badge">
            No. {{ String(memo.number).padStart(3, "0") }} s.
            {{ seriesYear(memo.date_issued) }}
          </span>
          <span class="memo-date">{{ memo.date_issued | shortDate }}</span>
        </div>
        <span class="memo-type-badge" :class="'type-' + (memo.type || 'office')">
          {{ memo.type === "hei" ? "HEI Memorandum" : "Office Memorandum" }}
        </span>
        <h3 class="memo-title">{{ memo.title }}</h3>
        <p v-if="memo.recipient" class="memo-recipient">
          <i class="bi bi-people mr-1"></i>{{ memo.recipient }}
        </p>
        <div class="memo-card-footer">
          <span v-if="memo.path" class="pdf-hint">
            <i class="bi bi-file-pdf mr-1"></i>PDF available
          </span>
          <span v-else class="pdf-hint text-muted">
            <i class="bi bi-file-earmark mr-1"></i>No PDF
          </span>
          <span class="click-hint"><i class="bi bi-arrow-right-circle mr-1"></i>Click to open</span>
        </div>
      </article>
    </div>

    <CreateMemorandumModal ref="CreateMemorandumModal" @saved="getRegionalMemos(false)" />
    <MemorandumDetailModal ref="detailModal" @edit="openEditFromModal" @deleted="getRegionalMemos(false)" />
  </div>
</template>

<script>
const moment = require("moment-business-days");
import CreateMemorandumModal from "./CreateMemorandumModal.vue";
import MemorandumDetailModal from "./MemorandumDetailModal.vue";

const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

export default {
  name: "RegionalMemos",
  components: {
    CreateMemorandumModal,
    MemorandumDetailModal,
  },
  filters: {
    shortDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleDateString("en-PH", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
  data() {
    return {
      tableLoader: false,
      memos: [],
      viewMode: "list",
      filterYear: "",
      filterMonth: "",
      filterType: "",
      searchQuery: "",
    };
  },
  computed: {
    canManage() {
      const role = this.$store.state.user && this.$store.state.user.role;
      return ["Administrator", "Chief", "RD"].includes(role);
    },
    monthOptions() {
      return MONTH_OPTIONS;
    },
    availableYears() {
      const years = new Set();
      this.memos.forEach((m) => {
        if (m.date_issued && moment(m.date_issued).isValid()) {
          years.add(moment(m.date_issued).year());
        }
      });
      return Array.from(years).sort((a, b) => b - a);
    },
    filteredMemos() {
      let list = this.memos;
      const year = this.filterYear === "" ? null : Number(this.filterYear);
      const month = this.filterMonth === "" ? null : Number(this.filterMonth);
      const q = (this.searchQuery || "").trim().toLowerCase();

      if (year !== null && !Number.isNaN(year)) {
        list = list.filter((m) => {
          if (!m.date_issued || !moment(m.date_issued).isValid()) return false;
          return moment(m.date_issued).year() === year;
        });
      }

      if (month !== null && !Number.isNaN(month)) {
        list = list.filter((m) => {
          if (!m.date_issued || !moment(m.date_issued).isValid()) return false;
          return moment(m.date_issued).month() + 1 === month;
        });
      }

      if (this.filterType) {
        list = list.filter((m) => (m.type || "office") === this.filterType);
      }

      if (q) {
        list = list.filter((m) => {
          const title = (m.title || "").toLowerCase();
          const num = String(m.number != null ? m.number : "").toLowerCase();
          return title.includes(q) || num.includes(q);
        });
      }

      return list;
    },
  },
  methods: {
    seriesYear(date) {
      if (!date || !moment(date).isValid()) return "—";
      return moment(date).year();
    },
    openDetail(memo) {
      this.$refs.detailModal.show(memo);
    },
    openEditFromModal(memo) {
      $("#memorandum-detail-modal").modal("hide");
      this.$nextTick(() => {
        this.$refs.CreateMemorandumModal.openEdit(memo);
      });
    },
    getRegionalMemos(toggle) {
      this.tableLoader = toggle;
      axios
        .get("api/memorandums")
        .then((res) => {
          this.memos = res.data || [];
        })
        .catch(() => {
          toastr.error("Failed to load memorandums.", "Error");
        })
        .finally(() => {
          this.tableLoader = false;
        });
    },
  },
  mounted() {
    this.getRegionalMemos(true);
  },
};
</script>

<style scoped>
.memos-page { padding: 0.25rem 0; }

.page-toolbar { gap: 1rem; }
.toolbar-filters { gap: 0.75rem; }

.filter-group label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.35px;
  text-transform: uppercase;
  color: #768192;
  margin-bottom: 0.35rem;
}
.filter-group .form-control {
  min-width: 150px;
  border-radius: 10px;
  min-height: 40px;
  border-color: rgba(0, 0, 21, 0.1);
}
.filter-search .search-wrap { position: relative; }
.filter-search .search-wrap i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #768192;
  pointer-events: none;
}
.filter-search input {
  padding-left: 2.2rem;
  min-width: 260px;
  border-radius: 10px;
  min-height: 40px;
  border-color: rgba(0,0,21,.1);
}

.result-count { font-size: 0.88rem; color: #768192; }

/* ── View toggle ── */
.view-toggle {
  display: inline-flex;
  border: 1px solid rgba(0,0,21,.12);
  border-radius: 10px;
  overflow: hidden;
}
.toggle-btn {
  background: transparent;
  border: none;
  padding: .38rem .65rem;
  cursor: pointer;
  font-size: .95rem;
  color: #768192;
  transition: background .15s, color .15s;
  line-height: 1;
}
.toggle-btn.active { background: #4f46e5; color: #fff; }
.toggle-btn:not(.active):hover { background: rgba(0,0,21,.05); }

/* ── List view ── */
.list-view-wrap {
  background: #fff;
  border: 1px solid rgba(0,0,21,.07);
  border-radius: 14px;
  overflow: hidden;
}
.list-table thead th {
  background: #f8f9fc;
  font-size: .72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .35px;
  color: #768192;
  border-bottom: 1px solid rgba(0,0,21,.08);
  padding: .65rem 1rem;
  white-space: nowrap;
}
.list-table tbody td {
  padding: .75rem 1rem;
  vertical-align: middle;
  border-color: rgba(0,0,21,.05);
}
.list-table tbody tr:last-child td { border-bottom: none; }
.list-row { cursor: pointer; }
.list-row:hover td { background: rgba(79,70,229,.03); }
.list-title { font-size: .9rem; font-weight: 600; color: #2b3a55; line-height: 1.4; }

/* ── Grid view ── */
.memo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
}
.memo-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 21, 0.06);
  border-radius: 14px;
  padding: 1.1rem 1.15rem;
  box-shadow: 0 4px 18px rgba(21, 38, 63, 0.04);
  transition: box-shadow .18s, transform .18s, border-color .18s;
  cursor: pointer;
  outline: none;
}
.memo-card:hover, .memo-card:focus {
  box-shadow: 0 8px 28px rgba(21, 38, 63, 0.1);
  transform: translateY(-2px);
  border-color: rgba(79,70,229,.25);
}
.memo-card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
}
.memo-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: .75rem;
  padding-top: .65rem;
  border-top: 1px solid rgba(0,0,21,.05);
}
.pdf-hint { font-size: .78rem; color: #4d8dff; }
.click-hint { font-size: .75rem; color: #a0aec0; }

/* ── Shared badges ── */
.memo-badge {
  display: inline-block;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  background: rgba(77, 141, 255, 0.1);
  color: #4d8dff;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}
.memo-date { font-size: 0.82rem; color: #768192; white-space: nowrap; }

.memo-type-badge {
  display: inline-block;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 0.5rem;
}
.memo-type-badge.type-office { background: rgba(43, 182, 163, 0.12); color: #1f8f7f; }
.memo-type-badge.type-hei    { background: rgba(224, 144, 31, 0.12);  color: #c97812; }

.memo-title {
  font-size: 0.98rem;
  font-weight: 600;
  color: #2b3a55;
  line-height: 1.45;
  margin-bottom: 0.5rem;
}
.memo-recipient { font-size: 0.84rem; color: #768192; margin-bottom: 0; }

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #768192;
  background: #fff;
  border-radius: 14px;
  border: 1px dashed rgba(0, 0, 21, 0.1);
}
.empty-icon { font-size: 2rem; display: block; margin-bottom: 0.5rem; opacity: 0.45; }
</style>
